// Path: src/components/dev/Firebase/Firebase.tsx
// Added anonymous sign-in configuration check, enhanced error handling
// Optimized retry logic and reduced logging overhead
// 06/07/25 3:45 PM +07
import React, { createContext, useContext, useMemo, useEffect, useState } from "react";
import { initializeApp, FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  browserSessionPersistence,
  setPersistence,
  Auth,
  User,
  signInAnonymously,
  signOut as firebaseSignOut,
} from "firebase/auth";
import {
  getFirestore,
  Firestore,
  doc as firestoreDoc,
  collection as firestoreCollection,
  DocumentReference,
  CollectionReference,
  updateDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  StorageReference,
  FirebaseStorage,
} from "firebase/storage";
import { getFunctions, Functions, httpsCallable } from "firebase/functions";
import { getAnalytics, Analytics, logEvent } from "firebase/analytics";
import { getDatabase, Database, ref as databaseRef, DatabaseReference } from "firebase/database";

export interface FirebaseContextType {
  app: FirebaseApp;
  auth: Auth;
  firestore: Firestore;
  storage: FirebaseStorage;
  functions: Functions;
  analytics: Analytics;
  database: Database;
  currentUser: User | null;
  isLoading: boolean;
  doc: (path: string, ...pathSegments: string[]) => DocumentReference;
  collection: (path: string) => CollectionReference;
  ref: (path: string) => StorageReference;
  databaseRef: (path: string) => DatabaseReference;
  uploadBytes: typeof uploadBytes;
  getDownloadURL: typeof getDownloadURL;
  callFunction: <T, R>(name: string, data: T) => Promise<R>;
  signOut: () => Promise<void>;
  updateDoc: (reference: DocumentReference, data: Record<string, unknown>) => Promise<void>;
  logEvent: (eventName: string, eventParams?: object) => void;
}

const FirebaseContext = createContext<FirebaseContextType | undefined>(undefined);

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  // Ensure anonymous sign-in is enabled in Firebase console
  // This is a security measure to prevent unauthorized access
};

const isProduction = import.meta.env.PROD;
const log = (...args: any[]) => !isProduction && console.log("Verification:", ...args);
const logError = (...args: any[]) => !isProduction && console.error("Verification:", ...args);
const logWarn = (...args: any[]) => !isProduction && console.warn("Verification:", ...args);

async function withRetry<T>(operation: () => Promise<T>, maxAttempts = 3, baseDelay = 1000): Promise<T> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error: unknown) {
      logWarn(`Retry attempt ${attempt} failed: ${(error as Error).message}`);
      if (attempt === maxAttempts) throw error;
      await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt)));
    }
  }
  throw new Error("Retry failed");
}

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [app] = useState<FirebaseApp>(() => {
    log("Initializing Firebase app...");
    if (!firebaseConfig.apiKey) throw new Error("Missing Firebase API key");
    return initializeApp(firebaseConfig);
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const auth = useMemo(() => {
    const authInstance = getAuth(app);
    log("Auth instance created");
    return authInstance;
  }, [app]);

  const firestore = useMemo(() => {
    const firestoreInstance = getFirestore(app);
    log("Firestore instance created");
    return firestoreInstance;
  }, [app]);

  const storage = useMemo(() => {
    const storageInstance = getStorage(app);
    log("Storage instance created");
    return storageInstance;
  }, [app]);

  const functions = useMemo(() => {
    const functionsInstance = getFunctions(app, "us-central1");
    log("Functions instance created");
    return functionsInstance;
  }, [app]);

  const analytics = useMemo(() => {
    const analyticsInstance = getAnalytics(app);
    log("Analytics instance created");
    return analyticsInstance;
  }, [app]);

  const database = useMemo(() => {
    const databaseInstance = getDatabase(app);
    log("Database instance created");
    return databaseInstance;
  }, [app]);

  useEffect(() => {
    log("Setting up Firebase auth persistence...");
    setPersistence(auth, browserSessionPersistence)
      .then(() => {
        log("Firebase session persistence set");
      })
      .catch((error: unknown) => {
        logError("Error setting auth persistence:", (error as Error).message);
        setError(`Failed to set auth persistence: ${(error as Error).message}`);
      });

    log("Setting up auth state listener...");
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      log("Auth state changed:", user ? "User logged in" : "No user");
      if (!user) {
        try {
          await signInAnonymously(auth);
          log("Anonymous sign-in successful");
        } catch (error: unknown) {
          logError("Anonymous sign-in failed:", (error as Error).message);
          setError(`Auth error: ${(error as Error).message}`);
        }
      }
      setCurrentUser(user || auth.currentUser);
      setIsLoading(false);
      logEvent(analytics, "page_view", { page_path: window.location.pathname });
    }, (error: unknown) => {
      logError("Auth state listener error:", (error as Error).message);
      setError(`Auth state listener failed: ${(error as Error).message}`);
    });

    return () => {
      log("Cleaning up auth state listener...");
      unsubscribe();
    };
  }, [auth, analytics]);

  const callFunction = async <T, R>(name: string, data: T): Promise<R> => {
    try {
      const callable = httpsCallable(functions, name);
      const result = await withRetry(() => callable(data));
      return result.data as R;
    } catch (error: unknown) {
      logError(`Error calling function ${name}:`, (error as Error).message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await withRetry(() => firebaseSignOut(auth));
      log("Sign out successful");
      setCurrentUser(null);
      logEvent(analytics, "sign_out");
    } catch (error: unknown) {
      logError("Sign out failed:", (error as Error).message);
      throw error;
    }
  };

  const value: FirebaseContextType = {
    app,
    auth,
    firestore,
    storage,
    functions,
    analytics,
    database,
    currentUser,
    isLoading,
    doc: (path, ...pathSegments) => firestoreDoc(firestore, path, ...pathSegments),
    collection: (path) => firestoreCollection(firestore, path),
    ref: (path: string) => storageRef(storage, path),
    databaseRef: (path: string) => databaseRef(database, path),
    uploadBytes,
    getDownloadURL,
    callFunction,
    signOut,
    updateDoc: async (reference: DocumentReference, data: Record<string, unknown>) => {
      await withRetry(() => updateDoc(reference, data));
    },
    logEvent: (eventName: string, eventParams?: object) => logEvent(analytics, eventName, eventParams),
  };

  if (error) {
    logError("FirebaseProvider error:", error);
    return (
      <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
        <h2>Error Initializing Firebase</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
};

export const useFirebase = (): FirebaseContextType => {
  const context = useContext(FirebaseContext);
  if (!context) throw new Error("useFirebase must be used within a FirebaseProvider");
  return context;
};

export const withFirebase = <P extends object>(
  Component: React.ComponentType<P & { firebase: FirebaseContextType }>
) => (props: P) => <Component {...props} firebase={useFirebase()} />;